import { Icon } from '@iconify/react';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import config from '../config';
import { toast } from 'react-toastify';
import ImageCropperModal from './common/ImageCropperModal';

const ViewProfileLayer = () => {
    const { user: currentUser, auth, updateUser } = useAuth();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');
    const navigate = useNavigate();

    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSyncing, setIsSyncing] = useState(!!sessionId); // Initialize based on URL
    const [error, setError] = useState(null);

    const [imagePreview, setImagePreview] = useState('/assets/images/user-grid/user-grid-img13.png');
    const [imageFile, setImageFile] = useState(null);
    const [coverImagePreview, setCoverImagePreview] = useState('/assets/images/user-grid/user-grid-bg1.png');
    const [coverImageFile, setCoverImageFile] = useState(null);

    // Image Cropper State
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        type: null, // 'profile' or 'cover'
        fixedAspectRatio: 1,
        initialAspectRatio: 1,
        minWidth: 750
    });

    const handleCropperHide = () => {
        // Revoke object URL to avoid memory leaks
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    };

    const handleCropComplete = (croppedFile, previewUrl) => {
        if (cropperState.type === 'profile') {
            setImageFile(croppedFile);
            setImagePreview(previewUrl);
        } else if (cropperState.type === 'cover') {
            setCoverImageFile(croppedFile);
            setCoverImagePreview(previewUrl);
        }
        handleCropperHide();
    };

    // Force Sync Subscription if returning from Stripe
    useEffect(() => {
        const syncSubscription = async () => {
            if (sessionId) {
                console.log('[ViewProfile] Starting subscription sync for session:', sessionId);
                setIsSyncing(true);
                
                const maxRetries = 3;
                let success = false;
                
                for (let retryCount = 0; retryCount < maxRetries; retryCount++) {
                    try {
                        const token = auth.getToken();
                        // Note: Backend expects 'session_id' (underscore) and 'sync=true'
                        const response = await fetch(`${config.API_BASE_URL}/api/payments/subscription?session_id=${sessionId}&sync=true`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        
                        if (!response.ok) {
                            throw new Error(`Sync failed with status: ${response.status}`);
                        }
                        
                        const data = await response.json();
                        
                        // Verify the subscription was actually updated
                        if (data.subscription) {
                            console.log('[ViewProfile] Subscription sync completed. Plan:', data.subscription.plan_name);
                            toast.success('Subscription updated successfully!');
                            success = true;
                            break;
                        } else {
                            throw new Error('No subscription returned from sync');
                        }
                    } catch (err) {
                        console.warn(`[ViewProfile] Sync attempt ${retryCount + 1} failed:`, err.message);
                        
                        if (retryCount < maxRetries - 1) {
                            // Wait before retry (exponential backoff)
                            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
                        }
                    }
                }
                
                if (!success) {
                    console.error('[ViewProfile] All sync attempts failed');
                    toast.warning('Payment received! Your subscription will update shortly.');
                }
                
                // Remove session_id from URL to prevent loop/re-sync
                navigate('/view-profile', { replace: true });
                setIsSyncing(false);
            }
        };
        syncSubscription();
    }, [sessionId, auth, navigate]);

    useEffect(() => {
        const fetchUser = async () => {
            // Block user fetch if we are currently syncing subscription
            if (isSyncing) {
                console.log('[ViewProfile] Waiting for subscription sync before fetching user profile...');
                return;
            }

            try {
                setLoading(true);
                const token = auth.getToken();
                const targetId = id || currentUser?.id;

                if (!targetId) {
                    setError('No user ID found');
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${config.API_BASE_URL}/api/users/${targetId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `Failed to fetch user profile (${response.status})`);
                }

                const data = await response.json();
                
                // Fetch position details if position_id exists
                let positionTitle = 'N/A';
                if (data.position_id) {
                    try {
                        const posResponse = await fetch(`${config.API_BASE_URL}/api/positions/${data.position_id}`, {
                             headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (posResponse.ok) {
                            const posData = await posResponse.json();
                            positionTitle = posData.title;
                        }
                    } catch (posErr) {
                        console.error('Error fetching position:', posErr);
                    }
                }
                
                setUserData({ ...data, position_title: positionTitle });
                
                if (data.profile_image) {
                    let imgPath = data.profile_image;
                    if (imgPath.startsWith('http')) {
                        setImagePreview(imgPath);
                    } else if (imgPath.includes('/avatar/')) {
                        setImagePreview(`${config.API_BASE_URL}${imgPath.startsWith('/') ? '' : '/'}${imgPath}`);
                    } else {
                        // Frontend asset
                        if (!imgPath.startsWith('/')) imgPath = '/' + imgPath;
                        setImagePreview(imgPath);
                    }
                } else {
                     // Keep default or set to placeholder
                     setImagePreview('/assets/images/user-grid/user-grid-img13.png');
                }

                if (data.cover_image) {
                    let coverPath = data.cover_image;
                    if (coverPath.startsWith('http')) {
                        setCoverImagePreview(coverPath);
                    } else if (coverPath.includes('/cover/')) {
                         setCoverImagePreview(`${config.API_BASE_URL}${coverPath.startsWith('/') ? '' : '/'}${coverPath}`);
                    } else {
                         if (!coverPath.startsWith('/')) coverPath = '/' + coverPath;
                         setCoverImagePreview(coverPath);
                    }
                } else {
                    setCoverImagePreview('/assets/images/user-grid/user-grid-bg1.png');
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [id, currentUser, isSyncing, auth]);

    const readURL = (input) => {
        if (input.target.files && input.target.files[0]) {
            const file = input.target.files[0];
            
            // Validate file size (10MB limit for source)
            if (file.size > 10 * 1024 * 1024) {
                toast.error("Image max size is 10MB");
                return;
            }
            
            const objectUrl = URL.createObjectURL(file);
            
            setCropperState({
                show: true,
                imageSrc: objectUrl,
                type: 'profile',
                fixedAspectRatio: 1, // 1:1 for profile
                initialAspectRatio: 1,
                minWidth: 400
            });
            
            // Reset input value so same file can be selected again if cancelled
            input.target.value = '';
        }
    };

    const readCoverURL = (input) => {
        if (input.target.files && input.target.files[0]) {
            const file = input.target.files[0];
            
            // Validate file size (10MB limit for source)
            if (file.size > 10 * 1024 * 1024) {
                toast.error("Cover image max size is 10MB");
                return;
            }
            
            const objectUrl = URL.createObjectURL(file);
            
            setCropperState({
                show: true,
                imageSrc: objectUrl,
                type: 'cover',
                fixedAspectRatio: 3/2, // 3:2 for cover (venue header/feed style)
                initialAspectRatio: 3/2,
                minWidth: 750
            });
            
            // Reset input value so same file can be selected again if cancelled
            input.target.value = '';
        }
    };

    const updateProfile = async (e) => {
        e.preventDefault();
        console.log("Submitting profile update...");
        setIsSaving(true);

        try {
            const formData = new FormData();
            // Use IDs or Names. Using elements by ID is safe if IDs are unique.
            // Mapping frontend IDs to backend fields:
            formData.append('full_name', e.target.elements['name'].value);
            formData.append('email', e.target.elements['email'].value);
            formData.append('phone', e.target.elements['number'].value);
            formData.append('language', e.target.elements['language'].value);
            formData.append('bio', e.target.elements['desc'].value);

            if (imageFile) {
                formData.append('profile_image', imageFile);
            }
            if (coverImageFile) {
                formData.append('cover_image', coverImageFile);
            }

            const token = auth.getToken();
            console.log(`Sending PUT request to: ${config.API_BASE_URL}/api/users/${userData.id}`);
            
            const response = await fetch(`${config.API_BASE_URL}/api/users/${userData.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Do NOT set Content-Type header when using FormData; fetch sets it automatically with boundary
                },
                body: formData
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || 'Failed to update profile');
            }

            const data = await response.json();
            console.log("Profile updated successfully:", data);
            
            setUserData(data.user); 
            toast.success('Profile updated successfully!');
            setImageFile(null); 
            
            // Optionally refresh the auth user if the current user updated their own profile
            if (currentUser && currentUser.id === userData.id) {
                updateUser(data.user);
            }

        } catch (err) {
            console.error("Profile update error:", err);
            toast.error(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (isSyncing) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Syncing...</span>
                </div>
                <h5 className="text-primary-light">Finalizing subscription update...</h5>
                <p className="text-secondary-light">Please wait while we confirm your payment details.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    if (!userData) return null;

    return (
        <div className="row gy-4">
            <div className="col-lg-4">
                <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
                    <div className="position-relative">
                        <label 
                            htmlFor="coverImageUpload" 
                            className="d-block w-100 cursor-pointer mb-0 position-relative"
                            title="Click to change cover image (Max 2MB)"
                        >
                            <img
                                src={coverImagePreview}
                                alt="Cover"
                                className="w-100 object-fit-cover"
                                style={{ height: '120px' }}
                            />
                            {/* Edit Cover Button Visual */}
                            <div 
                                className="position-absolute top-0 end-0 m-2 btn btn-sm btn-primary-600 rounded-circle p-0 d-flex align-items-center justify-content-center" 
                                style={{ width: '32px', height: '32px' }}
                            >
                                <Icon icon="solar:camera-outline" className="text-white text-lg" />
                            </div>
                        </label>
                        <input
                            type="file"
                            id="coverImageUpload"
                            accept=".png, .jpg, .jpeg"
                            hidden
                            onChange={readCoverURL}
                        />
                    </div>
                    <div className="pb-24 ms-16 mb-24 me-16  mt--100 position-relative z-1">
                        <div className="text-center border border-top-0 border-start-0 border-end-0">
                            <img
                                src={imagePreview}
                                alt=""
                                className="border br-white border-width-2-px w-200-px h-200-px rounded-circle object-fit-cover"
                            />
                            <h6 className="mb-0 mt-16">{userData.full_name}</h6>
                            <span className="text-secondary-light mb-16">{userData.email}</span>
                        </div>
                        <div className="mt-24">
                            <h6 className="text-xl mb-16">Personal Info</h6>
                            <ul>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        Full Name
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {userData.full_name}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        {" "}
                                        Email
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {userData.email}
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        {" "}
                                        Phone Number
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {userData.phone_number || 'N/A'}
                                    </span>
                                </li>
                                
                                <li className="d-flex align-items-center gap-1 mb-12">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        {" "}
                                        Languages
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : English
                                    </span>
                                </li>
                                <li className="d-flex align-items-center gap-1">
                                    <span className="w-30 text-md fw-semibold text-primary-light">
                                        {" "}
                                        Bio
                                    </span>
                                    <span className="w-70 text-secondary-light fw-medium">
                                        : {userData.bio || 'No bio available.'}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-lg-8">
                <div className="card h-100">
                    <div className="card-body p-24">
                        <ul
                            className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
                            id="pills-tab"
                            role="tablist"
                        >
                            <li className="nav-item" role="presentation">
                                <button
                                    className="nav-link d-flex align-items-center px-24 active"
                                    id="pills-edit-profile-tab"
                                    data-bs-toggle="pill"
                                    data-bs-target="#pills-edit-profile"
                                    type="button"
                                    role="tab"
                                    aria-controls="pills-edit-profile"
                                    aria-selected="true"
                                >
                                    Edit Profile
                                </button>
                            </li>
                        </ul>
                        <div className="tab-content" id="pills-tabContent">
                            <div
                                className="tab-pane fade show active"
                                id="pills-edit-profile"
                                role="tabpanel"
                                aria-labelledby="pills-edit-profile-tab"
                                tabIndex={0}
                            >
                                <h6 className="text-md text-primary-light mb-16">Profile Image</h6>
                                {/* Upload Image Start */}
                                <div className="mb-24 mt-16">
                                    <div className="avatar-upload">
                                        <div className="avatar-edit position-absolute bottom-0 end-0 me-24 mt-16 z-1 cursor-pointer">
                                            <input
                                                type="file"
                                                id="imageUpload"
                                                accept=".png, .jpg, .jpeg"
                                                hidden
                                                onChange={readURL}
                                            />
                                            <label
                                                htmlFor="imageUpload"
                                                className="w-32-px h-32-px d-flex justify-content-center align-items-center bg-primary-50 text-primary-600 border border-primary-600 bg-hover-primary-100 text-lg rounded-circle"
                                            >
                                                <Icon icon="solar:camera-outline" className="icon"></Icon>
                                            </label>
                                        </div>
                                        <div className="avatar-preview">
                                            <div
                                                id="imagePreview"
                                                style={{
                                                    backgroundImage: `url(${imagePreview})`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {/* Upload Image End */}
                                <form onSubmit={updateProfile}>
                                    <div className="row">
                                        <div className="col-sm-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="name"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Full Name
                                                    <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8"
                                                    id="name"
                                                    placeholder="Enter Full Name"
                                                    defaultValue={userData.full_name}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="email"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Email <span className="text-danger-600">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    className="form-control radius-8"
                                                    id="email"
                                                    placeholder="Enter Email Address"
                                                    defaultValue={userData.email}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="number"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Phone
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control radius-8"
                                                    id="number"
                                                    name="phone"
                                                    placeholder="Enter phone number"
                                                    defaultValue={userData.phone_number}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="language"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Language
                                                    <span className="text-danger-600">*</span>{" "}
                                                </label>
                                                <select
                                                    className="form-control radius-8 form-select"
                                                    id="language"
                                                    defaultValue="English"
                                                >
                                                    <option value="English">English</option>
                                                    <option value="Bangla">Bangla</option>
                                                    <option value="Hindi">Hindi</option>
                                                    <option value="Urdu">Urdu</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-sm-12">
                                            <div className="mb-20">
                                                <label
                                                    htmlFor="desc"
                                                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                                                >
                                                    Description
                                                </label>
                                                <textarea
                                                    name="#0"
                                                    className="form-control radius-8"
                                                    id="desc"
                                                    placeholder="Write description..."
                                                    defaultValue={userData.bio}
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-center gap-3">
                                            <button
                                                type="button"
                                                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                                disabled={isSaving}
                                            >
                                                {isSaving ? 'Saving...' : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Image Cropper Modal */}
            {cropperState.show && (
                <ImageCropperModal
                    show={cropperState.show}
                    onHide={handleCropperHide}
                    imageSrc={cropperState.imageSrc}
                    onCropComplete={handleCropComplete}
                    fixedAspectRatio={cropperState.fixedAspectRatio}
                    initialAspectRatio={cropperState.initialAspectRatio}
                    minWidth={cropperState.minWidth}
                />
            )}
        </div>
    );
};

export default ViewProfileLayer;
